import { create } from 'zustand';
import { BRICK_SPECS, WallModel, effectiveSize } from '@bw/core';
import type { BrickId, BrickKind, BrickPattern, Rotation } from '@bw/core';
import { HistoryStore, ProjectDocument } from '@bw/document';
import type { LibraryEntry } from '@bw/document';
import type { ToolId } from './tools.js';
import { COPY } from '../copy.js';

export interface FileBinding {
  /** A retained File System Access handle, when the browser has the API. */
  readonly handle: FileSystemFileHandle | null;
  readonly name: string;
}

export interface EditorState {
  // ---- document ----
  project: ProjectDocument;
  history: HistoryStore;
  /** Bumped whenever the document changes, so React re-reads it. */
  docRevision: number;
  savedRevision: number;
  file: FileBinding | null;

  // ---- session ----
  tool: ToolId;
  previousTool: ToolId;
  selectedKind: BrickKind;
  previewRotation: Rotation;
  activeMaterialId: string;
  snapEnabled: boolean;
  /**
   * Set a wall's courses out from the corners it meets, so a bonded quoin
   * comes out of the fill rather than having to be patched in afterwards.
   */
  bondCorners: boolean;
  selected: ReadonlySet<BrickId>;
  clipboard: BrickPattern | null;
  status: string;
  statusSticky: boolean;
  /** What assistive technology should hear; not every status line. */
  announcement: string;
  library: LibraryEntry[];
  leftPanelOpen: boolean;
  rightPanelOpen: boolean;
  theme: 'light' | 'dark' | 'system';
  busy: string | null;
  /** The welcome card is dismissed once the user picks a way to start. */
  welcomeDismissed: boolean;
  /** True while the full-screen 3D view is up (phase 2, M6). */
  view3d: boolean;

  // ---- actions ----
  touch: () => void;
  setProject: (project: ProjectDocument, file: FileBinding | null) => void;
  markSaved: () => void;
  setTool: (tool: ToolId) => void;
  setSelectedKind: (kind: BrickKind) => void;
  rotatePreview: () => void;
  setActiveMaterial: (id: string) => void;
  setSnapEnabled: (value: boolean) => void;
  setBondCorners: (value: boolean) => void;
  setSelected: (ids: ReadonlySet<BrickId>) => void;
  setClipboard: (pattern: BrickPattern | null) => void;
  setStatus: (text: string, sticky?: boolean, announce?: boolean) => void;
  setLibrary: (entries: LibraryEntry[]) => void;
  togglePanel: (side: 'left' | 'right') => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setBusy: (label: string | null) => void;
  dismissWelcome: () => void;
  setView3D: (open: boolean) => void;
}

/**
 * The document the app starts with.
 *
 * Held in a constant so `savedRevision` can be seeded from it. Building a
 * project bumps its revision — the constructor adds the first wall — so
 * hard-coding a saved revision of zero made a brand-new, untouched project
 * report unsaved changes, and every New or Open prompted to discard nothing.
 */
const startingProject = new ProjectDocument();

export const useEditor = create<EditorState>((set, get) => ({
  project: startingProject,
  history: new HistoryStore(),
  docRevision: 0,
  savedRevision: startingProject.revision,
  file: null,

  tool: 'build',
  previousTool: 'select',
  selectedKind: 'stretcher',
  previewRotation: 0,
  activeMaterialId: 'm1',
  snapEnabled: true,
  bondCorners: true,
  selected: new Set<BrickId>(),
  clipboard: null,
  status: COPY.launch,
  statusSticky: true,
  announcement: '',
  library: [],
  leftPanelOpen: true,
  rightPanelOpen: true,
  theme: 'system',
  busy: null,
  welcomeDismissed: false,
  view3d: false,

  touch: () => set((state) => ({ docRevision: state.docRevision + 1 })),

  setProject: (project, file) =>
    set({
      project,
      file,
      history: new HistoryStore(),
      selected: new Set<BrickId>(),
      clipboard: null,
      docRevision: get().docRevision + 1,
      savedRevision: project.revision,
      welcomeDismissed: false,
      view3d: false,
      activeMaterialId: project.materials[0]?.id ?? 'm1',
    }),

  markSaved: () => set((state) => ({ savedRevision: state.project.revision })),

  setTool: (tool) =>
    set((state) => (tool === state.tool ? {} : { tool, previousTool: state.tool })),

  setSelectedKind: (selectedKind) => set({ selectedKind, tool: 'build' }),

  rotatePreview: () =>
    set((state) => ({ previewRotation: state.previewRotation === 0 ? 90 : 0, tool: 'build' })),

  setActiveMaterial: (activeMaterialId) => set({ activeMaterialId }),
  setSnapEnabled: (snapEnabled) => set({ snapEnabled }),
  setBondCorners: (bondCorners) => set({ bondCorners }),
  setSelected: (selected) => set({ selected }),
  setClipboard: (clipboard) => set({ clipboard }),
  setStatus: (status, sticky = false, announce = true) =>
    set(announce ? { status, statusSticky: sticky, announcement: status } : { status, statusSticky: sticky }),
  setLibrary: (library) => set({ library }),
  togglePanel: (side) =>
    set((state) =>
      side === 'left'
        ? { leftPanelOpen: !state.leftPanelOpen }
        : { rightPanelOpen: !state.rightPanelOpen },
    ),
  setTheme: (theme) => set({ theme }),
  setBusy: (busy) => set({ busy }),
  dismissWelcome: () => set({ welcomeDismissed: true }),
  setView3D: (view3d) => set({ view3d }),
}));

/** The wall currently being edited. */
export function activeModel(state: EditorState): WallModel {
  return state.project.activeWall.model;
}

export function previewSize(state: EditorState): readonly [number, number] {
  return effectiveSize(state.selectedKind, state.previewRotation);
}

export function brickName(kind: BrickKind): string {
  return BRICK_SPECS[kind].name;
}

/** Design D4 — O(1) dirty tracking against a revision counter. */
export function isDirty(state: EditorState): boolean {
  return state.project.revision !== state.savedRevision;
}
